import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getWorkouts,
  saveWorkout as localSaveWorkout,
  deleteWorkout as localDeleteWorkout,
} from '../utils/fitnessData';
import { fsGetWorkouts, fsSaveWorkout, fsDeleteWorkout } from '../services/firestoreDb';
import { computeStreaks } from '../utils/streak';

export function useWorkouts() {
  const { user } = useAuth();
  const uid = user?.id;

  const [workouts, setWorkouts] = useState(() => getWorkouts());

  useEffect(() => {
    if (!uid) return;
    fsGetWorkouts(uid)
      .then((data) => {
        if (data !== null) {
          setWorkouts(data);
        }
      })
      .catch((err) => console.warn('[useWorkouts] Failed to load from Firestore:', err));
  }, [uid]);

  const saveWorkout = useCallback(
    async (workout) => {
      const entry = localSaveWorkout(workout);
      setWorkouts(getWorkouts());
      if (uid) {
        await fsSaveWorkout(uid, entry).catch((err) => console.warn('[useWorkouts] Save failed:', err));
      }
      return entry;
    },
    [uid]
  );

  const deleteWorkout = useCallback(
    async (id) => {
      localDeleteWorkout(id);
      setWorkouts(getWorkouts());
      if (uid) {
        await fsDeleteWorkout(uid, id).catch((err) => console.warn('[useWorkouts] Delete failed:', err));
      }
    },
    [uid]
  );

  const weekWorkouts = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    return workouts.filter((w) => new Date(w.date) >= cutoff);
  }, [workouts]);

  const totalDistance = useMemo(
    () => workouts.reduce((sum, w) => sum + (parseFloat(w.distance) || 0), 0),
    [workouts]
  );

  const totalDuration = useMemo(
    () => workouts.reduce((sum, w) => sum + (parseInt(w.duration) || 0), 0),
    [workouts]
  );

  const { currentStreak, longestStreak } = useMemo(() => {
    const dates = workouts.map((w) => (w.date || '').slice(0, 10)).filter(Boolean);
    return computeStreaks(dates);
  }, [workouts]);

  const getByType = useCallback(
    (type) => workouts.filter((w) => w.type === type),
    [workouts]
  );

  return { workouts, saveWorkout, deleteWorkout, weekWorkouts, totalDistance, totalDuration, currentStreak, longestStreak, getByType };
}
